// src/pages/ChartsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, DatePicker, Select, Row, Col, Typography, Space, Tabs, Button } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import { CalendarOutlined, LineChartOutlined } from '@ant-design/icons';
import { diaryAPI, doctorsAPI } from '../utils/api';
import { useAuth } from '../App';
import dayjs from 'dayjs';
import { useIsMobile } from '../hooks/useIsMobile';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const ChartsPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [weightData, setWeightData] = useState<any[]>([]);
  const [movementData, setMovementData] = useState<any[]>([]);
  const [abdomenData, setAbdomenData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('glucose');
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [patients, setPatients] = useState<any[]>([]);

  const getPatientDisplayName = useCallback((patient: any) => {
    const name = patient?.full_name?.trim?.() || patient?.full_name;
    if (!name || String(name).startsWith('gAAAA')) {
      return `Пациентка #${patient?.patient_id ?? patient?.id ?? '—'}`;
    }
    return name;
  }, []);

  const loadDoctorPatients = useCallback(async () => {
    try {
      const response = await doctorsAPI.getPatients();
      const patientsData = (response.data || []) as any[];
      setPatients(patientsData);
      if (patientsData.length > 0 && !selectedPatientId) {
        setSelectedPatientId(patientsData[0].patient_id);
      }
    } catch (error) {
      console.error('Error loading doctor patients:', error);
    }
  }, [selectedPatientId]);

  const loadChartData = useCallback(async () => {
    try {
      setLoading(true);
      const [fromDate, toDate] = dateRange;
      const params = { 
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD')
      };
      
      console.log('Loading chart data with params:', params);
      
      let glucoseResponse, mealResponse, weightResponse, movementResponse, abdomenResponse;

      if (user?.role === 'doctor' && selectedPatientId) {
        // Load data for selected patient - use getPatientDiary for abdomen
        glucoseResponse = await doctorsAPI.getPatientGlucoseEntries(selectedPatientId, params).catch((e) => { console.error('Error loading glucose:', e); return { data: [] }; });
        mealResponse = await doctorsAPI.getPatientMealEntries(selectedPatientId, params).catch((e) => { console.error('Error loading meals:', e); return { data: [] }; });
        weightResponse = await doctorsAPI.getPatientWeightEntries(selectedPatientId, params).catch((e) => { console.error('Error loading weight:', e); return { data: [] }; });
        movementResponse = await doctorsAPI.getPatientMovementEntries(selectedPatientId, params).catch((e) => { console.error('Error loading movements:', e); return { data: [] }; });
        abdomenResponse = await doctorsAPI.getPatientAbdomenEntries(selectedPatientId, params).catch((e) => { console.error('Error loading abdomen:', e); return { data: [] }; });
      } else if (user?.role === 'patient') {
        // Load data for current patient
        glucoseResponse = await diaryAPI.getGlucoseEntries(params).catch((e) => { console.error('Error loading glucose:', e); return { data: [] }; });
        mealResponse = await diaryAPI.getMealEntries(params).catch((e) => { console.error('Error loading meals:', e); return { data: [] }; });
        weightResponse = await diaryAPI.getWeightEntries(params).catch((e) => { console.error('Error loading weight:', e); return { data: [] }; });
        movementResponse = await diaryAPI.getMovementEntries(params).catch((e) => { console.error('Error loading movements:', e); return { data: [] }; });
        abdomenResponse = await diaryAPI.getAbdomenEntries(params).catch((e) => { console.error('Error loading abdomen:', e); return { data: [] }; });
      } else {
        // No data
        glucoseResponse = { data: [] };
        mealResponse = { data: [] };
        weightResponse = { data: [] };
        movementResponse = { data: [] };
        abdomenResponse = { data: [] };
      }
      
      console.log('All responses:', {
        glucose: Array.isArray(glucoseResponse.data) ? glucoseResponse.data.length : 0,
        meal: Array.isArray(mealResponse.data) ? mealResponse.data.length : 0,
        weight: Array.isArray(weightResponse.data) ? weightResponse.data.length : 0,
        movement: Array.isArray(movementResponse.data) ? movementResponse.data.length : 0,
        abdomen: Array.isArray(abdomenResponse.data) ? abdomenResponse.data.length : 0,
      });

      // Process glucose entries
      const glucoseEntries = (glucoseResponse.data as any[]) || [];
      console.log('Glucose entries:', glucoseEntries);
      const processedGlucose = glucoseEntries
        .map((entry: any) => ({
          date: dayjs(entry.timestamp).format('DD.MM'),
          dateValue: dayjs(entry.timestamp).valueOf(), // For sorting
          glucose_level: entry.glucose_level,
          meal_type: entry.meal_type,
          measurement_time: entry.measurement_time,
        }))
        .sort((a, b) => a.dateValue - b.dateValue) // Sort by date ascending
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting
      console.log('Processed glucose data:', processedGlucose);
      setChartData(processedGlucose);

      // Process meal entries
      // Process weight entries
      const weightEntries = (weightResponse.data as any[]) || [];
      console.log('Weight entries:', weightEntries);
      const processedWeight = weightEntries
        .map((entry: any) => ({
          date: dayjs(entry.entry_date || entry.timestamp).format('DD.MM'),
          dateValue: dayjs(entry.entry_date || entry.timestamp).valueOf(), // For sorting
          weight: entry.weight_kg,
        }))
        .sort((a, b) => a.dateValue - b.dateValue) // Sort by date ascending
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting
      console.log('Processed weight data:', processedWeight);
      setWeightData(processedWeight);

      // Process movement entries
      const movementEntries = (movementResponse.data as any[]) || [];
      const movementCounts: Record<string, { count: number; dateValue: number }> = {};
      movementEntries.forEach((entry: any) => {
        const date = dayjs(entry.entry_date || entry.movement_time).format('DD.MM');
        const dateValue = dayjs(entry.entry_date || entry.movement_time).valueOf();
        if (!movementCounts[date]) {
          movementCounts[date] = { count: 0, dateValue };
        }
        movementCounts[date].count += 1;
      });
      const processedMovement = Object.entries(movementCounts)
        .map(([date, data]) => ({
          date,
          count: data.count,
          dateValue: data.dateValue,
        }))
        .sort((a, b) => a.dateValue - b.dateValue) // Sort by date ascending
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting
      setMovementData(processedMovement);

      // Process abdomen entries
      const abdomenEntries = (abdomenResponse.data as any[]) || [];
      const processedAbdomen = abdomenEntries
        .map((entry: any) => ({
          date: dayjs(entry.entry_date || entry.timestamp).format('DD.MM'),
          dateValue: dayjs(entry.entry_date || entry.timestamp).valueOf(), // For sorting
          circumference: entry.abdomen_cm,
        }))
        .sort((a, b) => a.dateValue - b.dateValue) // Sort by date ascending
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting
      setAbdomenData(processedAbdomen);
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedPatientId, user?.role]);

  useEffect(() => {
    if (user?.role === 'doctor') {
      loadDoctorPatients();
    }
  }, [user, loadDoctorPatients]);

  useEffect(() => {
    // Load initial data on mount
    loadChartData();
  }, [loadChartData]);

  const timeRangeOptions = [
    { label: '7 дней', value: '7d' },
    { label: '30 дней', value: '30d' },
    { label: '3 месяца', value: '3m' },
    { label: '6 месяцев', value: '6m' },
  ];

  // Custom tooltip for glucose chart
  const GlucoseTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '12px', 
          border: '1px solid #ccc', 
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          <p style={{ margin: '4px 0', color: '#7CB342' }}>
            Уровень глюкозы: {payload[0].value} ммоль/л
          </p>
          <p style={{ margin: '4px 0' }}>
            Прием пищи: {payload[0].payload.meal_type === 'breakfast' ? 'Завтрак' : 
                        payload[0].payload.meal_type === 'lunch' ? 'Обед' : 
                        payload[0].payload.meal_type === 'dinner' ? 'Ужин' : 'Перекус'}
          </p>
          <p style={{ margin: '4px 0' }}>
            Время: {payload[0].payload.measurement_time === 'before' ? 'До еды' : 
                   payload[0].payload.measurement_time === '1h_after' ? 'Через 1ч' : 'Через 2ч'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Графики и аналитика</Title>
        <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
          {user?.role === 'doctor' ? 'Графики пациентки по выбранному периоду' : 'Визуализация вашего здоровья и питания'}
        </Text>
      </div>

      {user?.role === 'doctor' && (
        <Card style={{ marginBottom: 24, borderRadius: '12px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0, color: '#263238' }}>Выбор пациента</Title>
            </Col>
            <Col>
              <Select
                placeholder="Выберите пациента"
                style={{ width: 300 }}
                onChange={(value) => setSelectedPatientId(value)}
                value={selectedPatientId}
                options={patients.map((p) => ({ value: p.patient_id, label: getPatientDisplayName(p) }))}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Controls - Show for all tabs */}
      <Card style={{ borderRadius: '12px', marginBottom: isMobile ? 16 : 24 }}>
        {isMobile ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong style={{ color: '#263238', display: 'block', marginBottom: 8 }}>Период</Text>
              <Select
                defaultValue="7d"
                value={timeRange}
                options={timeRangeOptions}
                onChange={(value) => {
                  setTimeRange(value);
                  const days = value === '7d' ? 7 : value === '30d' ? 30 : value === '3m' ? 90 : 180;
                  setDateRange([dayjs().subtract(days, 'day'), dayjs()]);
                }}
                style={{ width: '100%' }}
                size="large"
              />
            </div>
            <RangePicker 
              size="large" 
              suffixIcon={<CalendarOutlined />} 
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
            />
            <Button
              type="primary"
              icon={<LineChartOutlined />}
              size="large"
              onClick={loadChartData}
              loading={loading}
              style={{ backgroundColor: '#7CB342', borderColor: '#7CB342', width: '100%' }}
              block
            >
              Построить график
            </Button>
            <div>
              <Text strong style={{ color: '#263238', display: 'block', marginBottom: 8 }}>Выберите график</Text>
              <Select
                value={activeTab}
                onChange={setActiveTab}
                style={{ width: '100%' }}
                size="large"
                options={[
                  { label: 'Глюкоза', value: 'glucose' },
                  { label: 'Вес', value: 'weight' },
                  { label: 'Шевеления', value: 'movement' },
                  { label: 'Окружность живота', value: 'abdomen' },
                ]}
              />
            </div>
          </Space>
        ) : (
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0, color: '#263238' }}>Период</Title>
            </Col>
            <Col>
              <Space>
                <Select
                  defaultValue="7d"
                  value={timeRange}
                  options={timeRangeOptions}
                  onChange={(value) => {
                    setTimeRange(value);
                    const days = value === '7d' ? 7 : value === '30d' ? 30 : value === '3m' ? 90 : 180;
                    setDateRange([dayjs().subtract(days, 'day'), dayjs()]);
                  }}
                  style={{ width: 150 }}
                  size="large"
                />
                <RangePicker 
                  size="large" 
                  suffixIcon={<CalendarOutlined />} 
                  style={{ minWidth: 220 }}
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange([dates[0], dates[1]]);
                    }
                  }}
                />
                <Button
                  type="primary"
                  icon={<LineChartOutlined />}
                  size="large"
                  onClick={loadChartData}
                  loading={loading}
                  style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
                >
                  Построить график
                </Button>
              </Space>
            </Col>
          </Row>
        )}
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        {isMobile ? (
          <div>
            {/* Render only active tab content on mobile */}
            {activeTab === 'glucose' && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card title="Уровень глюкозы" style={{ borderRadius: '12px' }}>
                  {loading ? (
                    <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text>Загрузка данных...</Text>
                    </div>
                  ) : chartData.length === 0 ? (
                    <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text type="secondary">Нет данных за выбранный период</Text>
                    </div>
                  ) : (
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} domain={['dataMin - 1', 'dataMax + 1']} />
                          <Tooltip content={<GlucoseTooltip />} />
                          <Legend />
                          <Line type="monotone" dataKey="glucose_level" stroke="#7CB342" activeDot={{ r: 6 }} strokeWidth={2} name="Уровень глюкозы (ммоль/л)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div style={{ marginTop: 16 }}>
                    <Text strong style={{ color: '#263238', fontSize: '12px' }}>Нормальные значения:</Text>
                    <ul style={{ color: '#5A7B8C', margin: '8px 0 0 16px', fontSize: '12px' }}>
                      <li>Натощак: 3.3-5.5 ммоль/л</li>
                      <li>Через 1-2 часа после еды: менее 7.8 ммоль/л</li>
                    </ul>
                  </div>
                </Card>
              </Space>
            )}
            {activeTab === 'weight' && (
              <Card title="Динамика веса" style={{ borderRadius: '12px' }}>
                {loading ? (
                  <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text>Загрузка данных...</Text>
                  </div>
                ) : weightData.length === 0 ? (
                  <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text type="secondary">Нет данных за выбранный период</Text>
                  </div>
                ) : (
                  <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={weightData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} domain={['dataMin - 1', 'dataMax + 1']} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="weight" stroke="#722ed1" fill="#722ed1" fillOpacity={0.6} name="Вес (кг)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            )}
            {activeTab === 'movement' && (
              <Card title="Количество шевелений в день" style={{ borderRadius: '12px' }}>
                {loading ? (
                  <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text>Загрузка данных...</Text>
                  </div>
                ) : movementData.length === 0 ? (
                  <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text type="secondary">Нет данных за выбранный период</Text>
                  </div>
                ) : (
                  <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={movementData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#7CB342" name="Количество шевелений" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            )}
            {activeTab === 'abdomen' && (
              <Card title="Динамика окружности живота" style={{ borderRadius: '12px' }}>
                {loading ? (
                  <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text>Загрузка данных...</Text>
                  </div>
                ) : abdomenData.length === 0 ? (
                  <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text type="secondary">Нет данных за выбранный период</Text>
                  </div>
                ) : (
                  <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={abdomenData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C', fontSize: 10 }} domain={['dataMin - 1', 'dataMax + 1']} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="circumference" stroke="#F4A261" fill="#F4A261" fillOpacity={0.6} name="Окружность живота (см)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            )}
          </div>
        ) : (
          <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <Tabs.TabPane tab="Глюкоза" key="glucose">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>

              {/* Glucose Chart */}
              <Card title="Уровень глюкозы" style={{ borderRadius: '12px' }}>
                {loading ? (
                  <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text>Загрузка данных...</Text>
                  </div>
                ) : chartData.length === 0 ? (
                  <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text type="secondary">Нет данных за выбранный период</Text>
                  </div>
                ) : (
                  <div style={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#5A7B8C"
                          tick={{ fill: '#5A7B8C', fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="#5A7B8C"
                          tick={{ fill: '#5A7B8C' }}
                          domain={['dataMin - 1', 'dataMax + 1']}
                        />
                        <Tooltip content={<GlucoseTooltip />} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="glucose_level" 
                          stroke="#7CB342" 
                          activeDot={{ r: 8 }} 
                          strokeWidth={2}
                          name="Уровень глюкозы (ммоль/л)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
          <div style={{ marginTop: 16 }}>
            <Text strong style={{ color: '#263238' }}>Нормальные значения:</Text>
            <ul style={{ color: '#5A7B8C', margin: '8px 0 0 16px' }}>
              <li>Натощак: 3.3-5.5 ммоль/л</li>
              <li>Через 1-2 часа после еды: менее 7.8 ммоль/л</li>
            </ul>
          </div>
        </Card>
      </Space>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Вес" key="weight">
            <Card title="Динамика веса" style={{ borderRadius: '12px' }}>
              {loading ? (
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text>Загрузка данных...</Text>
                </div>
              ) : weightData.length === 0 ? (
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">Нет данных за выбранный период</Text>
                </div>
              ) : (
                <div style={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={weightData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#5A7B8C" 
                        tick={{ fill: '#5A7B8C', fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C' }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="weight" stroke="#722ed1" fill="#722ed1" fillOpacity={0.6} name="Вес (кг)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Шевеления" key="movement">
            <Card title="Количество шевелений в день" style={{ borderRadius: '12px' }}>
              <div style={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={movementData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" stroke="#5A7B8C" tick={{ fill: '#5A7B8C' }} />
                    <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C' }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#fa8c16" name="Количество шевелений" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Окружность живота" key="abdomen">
            <Card title="Динамика окружности живота" style={{ borderRadius: '12px' }}>
              <div style={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={abdomenData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" stroke="#5A7B8C" tick={{ fill: '#5A7B8C' }} />
                    <YAxis stroke="#5A7B8C" tick={{ fill: '#5A7B8C' }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="circumference" stroke="#13c2c2" strokeWidth={2} name="Окружность живота (см)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Tabs.TabPane>

        </Tabs>
        )}
      </Card>
    </div>
  );
};